import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { transform } from 'sucrase';

interface DynamicComponentProps {
  code: string;
  props?: Record<string, unknown>;
}

const transpileCode = (code: string): string => {
  try {
    const result = transform(code, {
      transforms: ['typescript', 'jsx'],
      jsxRuntime: 'classic',
      production: true,
    });
    return result.code;
  } catch (error) {
    throw new Error(`Transpilation failed: ${(error as Error).message}`);
  }
};

const createComponent = (transpiledCode: string): React.ComponentType<Record<string, unknown>> => {
  const wrappedCode = `
    ${transpiledCode}
    return typeof Component !== 'undefined' ? Component : null;
  `;

  const factory = new Function('React', 'motion', 'AnimatePresence', wrappedCode);
  const Component = factory(React, motion, AnimatePresence);
  
  if (!Component) {
    throw new Error('Component not found. Make sure to define a component named "Component".');
  }
  
  return Component;
};

export function DynamicComponent({ code, props = {} }: DynamicComponentProps) {
  const [error, setError] = useState<string | null>(null);
  const [Component, setComponent] = useState<React.ComponentType<Record<string, unknown>> | null>(null);

  useEffect(() => {
    try {
      setError(null);
      const transpiled = transpileCode(code);
      const comp = createComponent(transpiled);
      setComponent(() => comp);
    } catch (err) {
      setError((err as Error).message);
      setComponent(null);
    }
  }, [code]);

  if (error) {
    return (
      <div style={{
        background: 'rgba(248, 113, 113, 0.1)',
        border: '1px solid var(--color-error)',
        borderRadius: '6px',
        padding: '12px',
        fontFamily: 'var(--font-mono)',
        fontSize: '11px',
      }}>
        <div style={{
          color: 'var(--color-error)',
          marginBottom: '8px',
          fontWeight: 600,
          letterSpacing: '1px',
        }}>
          [RENDER_ERROR]
        </div>
        <div style={{ color: 'var(--color-text-dim)' }}>
          {error}
        </div>
      </div>
    );
  }

  if (!Component) {
    return (
      <div style={{
        padding: '12px',
        fontFamily: 'var(--font-mono)',
        fontSize: '10px',
        color: 'var(--color-text-dim)',
      }}>
        [LOADING_COMPONENT...]
      </div>
    );
  }

  return <Component {...props} />;
}

export default DynamicComponent;
